import { useCallback, useEffect, useRef, useState } from 'react';

export default function CountUp({
  to,
  from = 0,
  direction = 'up',
  delay = 0,
  duration = 2,
  className = '',
  startWhen = true,
  separator = '',
  onStart,
  onEnd
}) {
  const ref = useRef(null);
  const [count, setCount] = useState(direction === 'down' ? to : from);

  const getDecimalPlaces = num => {
    const str = num.toString();
    if (str.includes('.')) {
      const decimals = str.split('.')[1];
      if (parseInt(decimals) !== 0) {
        return decimals.length;
      }
    }
    return 0;
  };

  const maxDecimals = Math.max(getDecimalPlaces(from), getDecimalPlaces(to));

  const formatValue = useCallback(
    latest => {
      const hasDecimals = maxDecimals > 0;
      const options = {
        useGrouping: !!separator,
        minimumFractionDigits: hasDecimals ? maxDecimals : 0,
        maximumFractionDigits: hasDecimals ? maxDecimals : 0
      };
      const formattedNumber = Intl.NumberFormat('en-US', options).format(latest);
      return separator ? formattedNumber.replace(/,/g, separator) : formattedNumber;
    },
    [maxDecimals, separator]
  );

  useEffect(() => {
    if (!startWhen) return;

    const startTime = Date.now() + delay * 1000;
    const endTime = startTime + duration * 1000;
    const startValue = direction === 'down' ? to : from;
    const endValue = direction === 'down' ? from : to;

    if (typeof onStart === 'function') {
      setTimeout(onStart, delay * 1000);
    }

    const animate = () => {
      const now = Date.now();
      if (now < startTime) {
        requestAnimationFrame(animate);
        return;
      }

      if (now >= endTime) {
        setCount(endValue);
        if (typeof onEnd === 'function') onEnd();
        return;
      }

      const progress = (now - startTime) / (endTime - startTime);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      const current = startValue + (endValue - startValue) * eased;
      setCount(current);
      requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [from, to, direction, delay, duration, startWhen, onStart, onEnd]);

  return <span className={className} ref={ref}>{formatValue(count)}</span>;
}
